import { Box } from "@mui/material";

import { TableActions } from "../../components/v3";

import { Edit, Delete, NorthEast, SouthWest } from "@mui/icons-material";
import { getBalanceColor, TransactionTypes } from "../../utils";

/**
 * Route paths for the account module.
 * Single source of truth — reuse these instead of hard-coding `/accounts...` or `/account...` strings.
 */
export const ACCOUNT_ROUTES = {
  root: "/accounts",
  details: "/account/:id",
  invoice: "/account/invoice/:id",
} as const;

const buildTransactionActionsColumn = (handleActions, canEdit, canDelete) => ({
  field: "actions",
  headerName: "Action",
  flex: 0.8,
  sortable: false,
  renderCell: (params) => {
    const { type } = params.row;
    const actions = [];
    if ([TransactionTypes.ACCOUNT, TransactionTypes.WORKER, TransactionTypes.VENDOR].includes(type)) {
      if (canEdit) {
        actions.push({
          icon: Edit,
          type: "edit",
          label: "Edit",
          onClick: handleActions,
        });
      }
      if (canDelete) {
        actions.push({
          icon: Delete,
          type: "delete",
          label: "Delete",
          onClick: handleActions,
        });
      }
      return <TableActions item={params} actions={actions} />;
    }
  },
});

export const AccountTableColumns = [
  { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
  { field: "name", headerName: "Account Name", flex: 1.5, sortable: true },
  { field: "type", headerName: "Account Type", flex: 1, sortable: true },
  {
    field: "balance",
    headerName: "Balance",
    flex: 1,
    sortable: true,
    renderCell: (params) => {
      const value = Number(params.value);
      let color = "gray";

      if (value > 0) color = "green";
      else if (value < 0) color = "red";

      return <span style={{ color, fontWeight: 500 }}>₹ {Number(value)}</span>;
    },
  },
];

/**
 * Route paths for the account module.
 * Single source of truth — reuse these instead of hard-coding `/account...` strings.
 */
export const getTransactionTableColumns = (handleActions, canEdit, canDelete) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
    { field: "date", headerName: "Date", flex: 1, sortable: true, sortingOrder: ["desc", "asc", null] },
    { field: "fromOrToAccount", headerName: "From/To", flex: 1, sortable: false },
    { field: "description", headerName: "Remarks", flex: 1.5, sortable: true },
    {
      field: "cashIn",
      headerName: "in",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "green", display: "flex", alignItems: "center", gap: 4 }}>
            <SouthWest fontSize="small" />₹ {Number(params.value).toFixed(2)}
          </span>
        );
      },
    },
    {
      field: "cashOut",
      headerName: "out",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "red", display: "flex", alignItems: "center", gap: 4 }}>
            <NorthEast fontSize="small" />₹ {Number(params.value).toFixed(2)}
          </span>
        );
      },
    },
    // { field: "balance", headerName: "Balance", flex: 1 },
  ];
  if (canEdit || canDelete) {
    headers.push(buildTransactionActionsColumn(handleActions, canEdit, canDelete));
  }
  return headers;
};

export const getLedgerTransactionTableColumns = (handleActions, canEdit, canDelete) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
    { field: "date", headerName: "Date", flex: 1, sortable: false },
    { field: "fromOrToAccount", headerName: "From/To", flex: 1, sortable: false },
    { field: "description", headerName: "Remarks", flex: 1.5, sortable: false },
    {
      field: "cashIn",
      headerName: "in",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "green", display: "flex", alignItems: "center", gap: 4 }}>
            <SouthWest fontSize="small" />₹ {Number(params.value).toFixed(2)}
          </span>
        );
      },
    },
    {
      field: "cashOut",
      headerName: "out",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "red", display: "flex", alignItems: "center", gap: 4 }}>
            <NorthEast fontSize="small" />₹ {Number(params.value).toFixed(2)}
          </span>
        );
      },
    },
    {
      field: "balance",
      headerName: "Balance",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const rawBalance = params.value;
        if (rawBalance === null || rawBalance === undefined || rawBalance === "") return "-";
        const num = Number(rawBalance);
        const formatted = `${num < 0 ? "-" : ""}₹ ${Math.abs(num).toFixed(2)}`;
        return <Box sx={{ color: getBalanceColor(num), display: "flex", alignItems: "center" }}>{formatted}</Box>;
      },
    },
  ];

  if (canEdit || canDelete) {
    headers.push(buildTransactionActionsColumn(handleActions, canEdit, canDelete));
  }
  return headers;
};
